import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import SlaTicker from "@/components/app/SlaTicker";
import { useAuth } from "@/context/AuthContext";

const TONES = {
  NEW_REQUESTS: "text-slate-900",
  APPROVAL_PENDING: "text-amber-700",
  PACKING_KIT_PENDING: "text-amber-700",
  PICKUP_TO_SCHEDULE: "text-amber-700",
  FAILED_PICKUPS: "text-rose-700",
  SLA_BREACHED: "text-rose-700",
  READY_FOR_BILLING: "text-emerald-700",
  ORDER_ACTIVATED: "text-emerald-700",
};

export default function RecoveryControlTower() {
  const { user } = useAuth();
  const [tiles, setTiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeTile, setActiveTile] = useState(null);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/recovery/control-tower/tiles");
      setTiles(data.tiles || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openTile = async (t) => {
    setActiveTile(t);
    const statusMap = {
      NEW_REQUESTS: "SUBMITTED", APPROVAL_PENDING: null, ORDER_ACTIVATED: "ORDER_ACTIVATED",
      FAILED_PICKUPS: "PICKUP_FAILED", IN_TRANSIT: "IN_TRANSIT",
      WAREHOUSE_PENDING: "WAREHOUSE_RECEIPT_PENDING",
      INSPECTION_PENDING: "INSPECTION_IN_PROGRESS",
    };
    try {
      const { data } = await api.get("/recovery/requests",
        { params: statusMap[t.key] ? { status: statusMap[t.key] } : {} });
      setRows(data.items || []);
    } catch { setRows([]); }
  };

  return (
    <div className="max-w-[1600px] space-y-4" data-testid="recovery-control-tower">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Operations · Recover</div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 mt-1">Control Tower</h1>
          <p className="text-sm text-slate-500 mt-1">Live operational workload driven by real recovery-request states.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="ct-refresh">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {tiles.map((t) => (
          <button key={t.key} onClick={() => openTile(t)}
            data-testid={`ct-tile-${t.key}`}
            className={"text-left border rounded-md p-3 hover:shadow-sm hover:border-slate-400 transition-all " +
              (activeTile?.key === t.key ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200 bg-white")}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{t.label}</div>
            <div className={"text-2xl font-heading font-semibold mt-1 " + (TONES[t.key] || "text-slate-900")}>
              {t.count}
            </div>
          </button>
        ))}
      </div>

      {activeTile && (
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Filtered by</div>
              <h3 className="text-lg font-heading font-semibold text-slate-900">{activeTile.label}</h3>
            </div>
            <span className="text-[11px] text-slate-500">{rows.length} row(s)</span>
          </div>
          {rows.length === 0 ? (
            <div className="p-8 text-center text-[12px] text-slate-500">No orders in this state.</div>
          ) : (
            <div className="divide-y">
              {rows.slice(0, 50).map((r) => (
                <div key={r.id} className="py-2 flex items-center gap-3 text-[13px]"
                  data-testid={`ct-order-${r.id}`}>
                  <span className="font-mono text-slate-800 min-w-[180px]">
                    {r.order_code || r.request_code}
                  </span>
                  <span className="text-slate-700 flex-1 truncate">
                    {r.scope_profile?.name || r.scope_profile_code}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                    {r.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
